'use client';

import { Suspense, useRef, type ReactElement, type RefObject } from 'react';
import { useRouteLoaderData } from 'react-router';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { emptyCategoryProductCounts, type CategoryProductCounts } from '@/lib/category-product-counts';
import { getSearchFocusableElements } from '../search-focusable-utils';
import { CategorySuggestionColumn } from './category-suggestion-column';
import type { SuggestionItem, TrendingSearchItem } from './types';
import { highlightMatch } from './utils/highlight-match';
import { RowButton } from './components/row-button';
import { SectionTitle } from './components/section-title';
import { useTranslation } from 'react-i18next';
import { SvgClose, SvgHelp, SvgRedo, SvgSearch, SvgTrending } from '@/components/icons-eataly';
import CustomerCareLink from '@/components/customer-care-link';
import { RECENT_SEARCH_MIN_LENGTH } from '../search-constants';

type CategoryCountsLoaderData = {
    categoryProductCounts?: Promise<CategoryProductCounts | undefined>;
};
export type SearchSuggestionsPanelVariant = 'desktop' | 'mobile';

export default function SearchSuggestionsPanel({
    query,
    recentSearches,
    trendingItems,
    typingLeftItems,
    typingCategoryItems,
    onNavigate,
    onRemoveRecent,
    onClearRecent,
    variant = 'desktop',
    attachedToInput = false,
    className,
    minCharsForApiSuggestions = RECENT_SEARCH_MIN_LENGTH,
    returnFocusRef,
}: {
    query: string;
    recentSearches: string[];
    trendingItems: TrendingSearchItem[];
    typingLeftItems: SuggestionItem[];
    typingCategoryItems: SuggestionItem[];
    onNavigate: (link: string, options?: { persistQuery?: string }) => void;
    onRemoveRecent: (label: string) => void;
    onClearRecent: () => void;
    variant?: SearchSuggestionsPanelVariant;
    attachedToInput?: boolean;
    className?: string;
    /** Match `useSearchSuggestions` threshold: below this length, show a single “search for …” row instead of API columns. */
    minCharsForApiSuggestions?: number;
    /** Search input to receive focus when leaving the panel via Tab (overlay stays open). */
    returnFocusRef?: RefObject<HTMLInputElement | null>;
}): ReactElement {
    const trimmed = query.trim();
    const isTyping = trimmed.length > 0;
    const showApiTypingColumns = trimmed.length >= minCharsForApiSuggestions;
    const isMobile = variant === 'mobile';
    const { t } = useTranslation('search');
    const panelRef = useRef<HTMLDivElement>(null);
    const appData = useRouteLoaderData<CategoryCountsLoaderData>('routes/_app');
    const categoryProductCounts = appData?.categoryProductCounts ?? emptyCategoryProductCounts;

    const focusSearchInput = () => {
        requestAnimationFrame(() => returnFocusRef?.current?.focus());
    };

    const handlePanelKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab' || e.shiftKey || !returnFocusRef) return;

        const root = panelRef.current;
        if (!root) return;

        const focusables = getSearchFocusableElements(root);
        const last = focusables.at(-1);
        if (!last || document.activeElement !== last) return;

        e.preventDefault();
        e.stopPropagation();
        focusSearchInput();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Let Tab follow natural DOM order so users can reach remove buttons, "clear", and SOS footer.
        // Arrow keys provide quick navigation across suggestion rows.
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

        const root = e.currentTarget;
        const items = Array.from(root.querySelectorAll<HTMLButtonElement>('button[data-suggestion-item="true"]'));
        if (items.length === 0) return;

        const active = document.activeElement as HTMLElement | null;
        const currentIndex = active ? items.findIndex((el) => el === active) : -1;

        // Roving focus for arrow navigation.
        e.preventDefault();
        e.stopPropagation();

        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const nextIndex =
            currentIndex === -1
                ? delta > 0
                    ? 0
                    : items.length - 1
                : (currentIndex + delta + items.length) % items.length;
        items[nextIndex]?.focus();
    };

    return (
        <div
            ref={panelRef}
            onKeyDownCapture={handlePanelKeyDownCapture}
            className={cn(
                isMobile
                    ? 'bg-[var(--neutral-white)]'
                    : attachedToInput
                      ? 'bg-[var(--sand-95)] rounded-b-[var(--corner-16)] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.05)] overflow-hidden'
                      : 'bg-[var(--sand-95)] border border-[var(--sand-90)] rounded-b-[var(--corner-16)] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.05)] overflow-hidden',
                className
            )}>
            <div
                onKeyDown={handleKeyDown}
                className={cn(
                    'flex flex-col',
                    isMobile ? 'gap-2 px-6 py-0' : 'gap-4 px-6 py-4 md:px-10 md:py-4',
                    !isMobile && attachedToInput && 'border-x border-[var(--sand-90)]'
                )}>
                {isTyping && !showApiTypingColumns ? (
                    <div className="flex flex-col px-1">
                        <ul className="m-0 flex list-none flex-col p-0">
                            <li className="w-full list-none">
                                <RowButton
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() =>
                                        onNavigate(`/search?q=${encodeURIComponent(trimmed)}`, {
                                            persistQuery: trimmed,
                                        })
                                    }>
                                    <SvgSearch className="size-5 shrink-0" aria-hidden />
                                    <p className="flex-1 text-left text-[length:var(--font-size-200)] leading-[var(--line-height-200)] tracking-[0.14px] text-[color:var(--neutral-10)]">
                                        {highlightMatch(trimmed, trimmed)}
                                    </p>
                                </RowButton>
                            </li>
                        </ul>
                    </div>
                ) : isTyping ? (
                    <div
                        className={cn(
                            isMobile ? 'flex flex-col gap-2' : 'grid grid-cols-1 md:grid-cols-2 md:gap-x-[124px]'
                        )}>
                        <div className="flex min-w-0 flex-col gap-2 px-1">
                            {typingLeftItems.length > 0 ? (
                                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                                    {typingLeftItems.map((item) => (
                                        <li key={`${item.kind}-${item.id}`} className="w-full list-none">
                                            <RowButton
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() =>
                                                    onNavigate(item.link ?? '', { persistQuery: item.label })
                                                }>
                                                <SvgSearch className="size-5 shrink-0" aria-hidden />
                                                <p className="flex-1 text-left text-[length:var(--font-size-200)] leading-[var(--line-height-200)] tracking-[0.14px] text-[color:var(--neutral-10)]">
                                                    {highlightMatch(item.label, trimmed)}
                                                </p>
                                            </RowButton>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                        <div className={cn('flex min-w-0 flex-col gap-2 px-1', isMobile ? '' : 'md:mt-0')}>
                            {typingCategoryItems.length > 0 ? (
                                <Suspense fallback={null}>
                                    <CategorySuggestionColumn
                                        items={typingCategoryItems}
                                        countsPromise={categoryProductCounts}
                                        trimmedQuery={trimmed}
                                        onNavigate={(link) => onNavigate(link)}
                                    />
                                </Suspense>
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <div
                        className={cn(
                            isMobile ? 'flex flex-col gap-4' : 'grid grid-cols-1 md:grid-cols-2 md:gap-x-[124px]'
                        )}>
                        <div className="min-w-0 max-w-sm px-1">
                            <SectionTitle>{t('suggestions.recentSearches')}</SectionTitle>
                            <div className="flex flex-col">
                                {recentSearches.length > 0 ? (
                                    <ul className="m-0 flex list-none flex-col p-0">
                                        {recentSearches.map((label) => (
                                            <li key={label} className="w-full list-none">
                                                <div className="flex items-center gap-2">
                                                    <RowButton
                                                        aria-label={t('suggestions.recentSearchSuggestionAriaLabel', {
                                                            label,
                                                        })}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() =>
                                                            onNavigate(`/search?q=${encodeURIComponent(label)}`, {
                                                                persistQuery: label,
                                                            })
                                                        }>
                                                        <SvgRedo className="size-5 shrink-0" aria-hidden />
                                                        <p className="flex-1 text-left text-[length:var(--font-size-200)] leading-[var(--line-height-200)] tracking-[0.14px] text-[color:var(--neutral-10)]">
                                                            {label}
                                                        </p>
                                                    </RowButton>
                                                    <button
                                                        type="button"
                                                        className={cn(
                                                            'size-5 shrink-0 rounded-sm',
                                                            focusVisibleOutline
                                                        )}
                                                        aria-label={t('suggestions.removeRecentSearchAriaLabel', {
                                                            label,
                                                        })}
                                                        onClick={() => onRemoveRecent(label)}>
                                                        <SvgClose className="size-5 cursor-pointer" aria-hidden />
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="py-2 text-sm text-[color:var(--neutral-40)]">
                                        {t('suggestions.noRecentSearches')}
                                    </p>
                                )}
                                {recentSearches.length > 0 && (
                                    <button
                                        type="button"
                                        className="mt-2 cursor-pointer self-start text-sm text-[color:var(--neutral-10)] underline underline-offset-4"
                                        onClick={onClearRecent}>
                                        {t('suggestions.clearRecentSearches')}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className={cn('min-w-0 px-1', isMobile ? '' : 'mt-6 md:mt-0')}>
                            {trendingItems.length > 0 && <SectionTitle>{t('suggestions.trending')}</SectionTitle>}
                            {trendingItems.length > 0 ? (
                                <ul className="m-0 flex list-none flex-col p-0">
                                    {trendingItems.map((item) => (
                                        <li key={`${item.label}-${item.link}`} className="w-full list-none">
                                            <RowButton
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => onNavigate(item.link, { persistQuery: item.label })}>
                                                <SvgTrending className="size-5 shrink-0" aria-hidden />
                                                <p className="flex-1 text-left text-[length:var(--font-size-200)] leading-[var(--line-height-200)] tracking-[0.14px] text-[color:var(--neutral-10)]">
                                                    {item.label}
                                                </p>
                                            </RowButton>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>

            {/* SOS footer */}
            <CustomerCareLink
                kind="contacts"
                className={cn(
                    'flex items-center gap-2 text-body-copy-m-bold text-neutral-white bg-[var(--brown-20)]',
                    isMobile ? 'px-6 pb-6 pt-4 rounded-md' : 'px-10 py-3 rounded-b-lg'
                )}>
                <SvgHelp className="size-6 shrink-0 text-neutral-white" aria-hidden />
                <span className="flex-1">{t('suggestions.needHelp')}</span>
                <span className="cursor-pointer underline underline-offset-4">{t('suggestions.contactUs')}</span>
            </CustomerCareLink>
        </div>
    );
}
